import React,{useState} from 'react';
import {motion} from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import {resultsGuide,getCategoryFromScore,getPhaseScore} from '../data/resultsGuide';

const {FiTarget,FiTrendingUp,FiCheckCircle,FiAlertTriangle,FiChevronDown,FiChevronRight,FiClock,FiPlayCircle,FiFlag}=FiIcons;

const DetailedResults=({data})=> {
const [expandedSections,setExpandedSections]=useState({
category: true,
phases: false,
redFlags: false,
actionPlan: false
});

const category=getCategoryFromScore(data.totalScore,data.maxScore);
const categoryData=resultsGuide.categories[category];

const phaseScores={
foundation: getPhaseScore(data.answers,'foundation'),
clarity: getPhaseScore(data.answers,'clarity'),
action: getPhaseScore(data.answers,'action'),
support: getPhaseScore(data.answers,'support'),
optimization: getPhaseScore(data.answers,'optimization')
};

const toggleSection=(section)=> {
setExpandedSections(prev=> ({
...prev,
[section]: !prev[section]
}));
};

const getColorClasses=(color)=> {
const colorMap={
red: {bg: 'bg-red-100',text: 'text-red-700',border: 'border-red-200',accent: 'bg-red-500'},
yellow: {bg: 'bg-accent/10',text: 'text-accent',border: 'border-accent/20',accent: 'bg-accent'},
blue: {bg: 'bg-primary-100',text: 'text-primary-700',border: 'border-primary-200',accent: 'bg-primary-500'},
green: {bg: 'bg-sage-100',text: 'text-sage-700',border: 'border-sage-200',accent: 'bg-sage-500'}
};
return colorMap[color] || colorMap.blue;
};

const SectionHeader=({title,section,icon})=> (
<button
onClick={()=> toggleSection(section)}
className="w-full flex items-center justify-between p-4 bg-bg-light hover:bg-bg-cream rounded-xl transition-all border-2 border-gray-1 hover:border-sage-300"
>
<div className="flex items-center">
<SafeIcon icon={icon} className="mr-3 text-primary-600" />
<h3 className="text-lg font-semibold text-text-primary">{title}</h3>
</div>
<SafeIcon
icon={expandedSections[section] ? FiChevronDown : FiChevronRight}
className="text-text-secondary"
/>
</button>
);

return (
<div className="max-w-5xl mx-auto space-y-6">
{/* Category Analysis */}
<div className="bg-bg-primary rounded-2xl ernest-shadow overflow-hidden">
<SectionHeader
title="Your Bridge Framework™ Category"
section="category"
icon={FiTarget}
/>
{expandedSections.category && (
<motion.div
initial={{opacity: 0,height: 0}}
animate={{opacity: 1,height: 'auto'}}
className="p-6"
>
<div className={`${getColorClasses(categoryData.color).bg} ${getColorClasses(categoryData.color).border} border-2 rounded-xl p-6 mb-6`}>
<div className="text-center">
<div className={`inline-flex items-center px-4 py-2 ${getColorClasses(categoryData.color).accent} text-white rounded-full font-semibold mb-3`}>
🎯 {categoryData.title}
</div>
<p className="text-lg font-medium text-text-primary mb-2">
{categoryData.subtitle}
</p>
<p className="text-text-secondary leading-relaxed">
{categoryData.description}
</p>
</div>
</div>

<div className="grid md:grid-cols-2 gap-6">
<div className="bg-bg-light rounded-xl p-6 border-2 border-gray-1">
<h4 className="font-semibold text-text-primary mb-4 flex items-center">
<SafeIcon icon={FiClock} className="mr-2 text-primary-600" />
Your Timeline
</h4>
<p className="text-text-secondary font-medium">{categoryData.timeline}</p>
{categoryData.transformationTimeline && (
<div className="mt-4 space-y-2">
<p className="text-sm font-medium text-text-secondary mb-2">Transformation Schedule:</p>
{categoryData.transformationTimeline.map((item,index)=> (
<div key={index} className="flex justify-between text-sm">
<span className="text-text-secondary">Weeks {item.weeks}:</span>
<span className="text-text-primary font-medium">{item.phase}</span>
</div>
))}
</div>
)}
</div>

<div className="bg-bg-light rounded-xl p-6 border-2 border-gray-1">
<h4 className="font-semibold text-text-primary mb-4 flex items-center">
<SafeIcon icon={FiPlayCircle} className="mr-2 text-sage-600" />
Immediate Actions
</h4>
<ul className="space-y-2">
{categoryData.immediateActions?.map((action,index)=> (
<li key={index} className="flex items-start text-sm">
<SafeIcon icon={FiCheckCircle} className="mr-2 mt-0.5 text-sage-500 flex-shrink-0" />
<span className="text-text-secondary">{action}</span>
</li>
))}
</ul>
</div>
</div>

{categoryData.expectedResults && (
<div className="mt-6 bg-gradient-to-r from-bg-cream to-primary-50 rounded-xl p-6 border border-accent/20">
<h4 className="font-semibold text-text-primary mb-4 flex items-center">
<SafeIcon icon={FiTrendingUp} className="mr-2 text-sage-600" />
Expected Results
</h4>
<div className="grid md:grid-cols-2 gap-3">
{categoryData.expectedResults.map((result,index)=> (
<div key={index} className="flex items-start">
<SafeIcon icon={FiTarget} className="mr-2 mt-0.5 text-primary-500 flex-shrink-0" />
<span className="text-sm text-text-secondary">{result}</span>
</div>
))}
</div>
</div>
)}
</motion.div>
)}
</div>

{/* Phase Analysis */}
<div className="bg-bg-primary rounded-2xl ernest-shadow overflow-hidden">
<SectionHeader
title="Bridge Framework™ Phase Analysis"
section="phases"
icon={FiTrendingUp}
/>
{expandedSections.phases && (
<motion.div
initial={{opacity: 0,height: 0}}
animate={{opacity: 1,height: 'auto'}}
className="p-6"
>
<div className="space-y-6">
{Object.entries(phaseScores).map(([phaseKey,phaseData])=> {
const phaseInfo=resultsGuide.phaseAnalysis[phaseKey];
const levelColors={
urgent: 'red',
high: 'yellow',
moderate: 'blue',
low: 'green',
critical: 'red',
minor: 'blue',
strong: 'green'
};
const levelColor=levelColors[phaseData.level] || 'blue';

return (
<div key={phaseKey} className="border-2 border-gray-1 rounded-xl p-6 hover:border-sage-300 transition-all">
<div className="flex items-center justify-between mb-4">
<h4 className="text-lg font-semibold text-text-primary">
{phaseInfo.title}
</h4>
<span className={`px-3 py-1 rounded-full text-sm font-medium border-2 ${getColorClasses(levelColor).bg} ${getColorClasses(levelColor).text} border-current`}>
{phaseData.level.charAt(0).toUpperCase() + phaseData.level.slice(1)} Priority
</span>
</div>

<div className="mb-4">
<div className="flex justify-between text-sm text-text-secondary mb-2">
<span>Score: {phaseData.score} / {phaseData.maxScore}</span>
<span>{Math.round((phaseData.score / phaseData.maxScore) * 100)}%</span>
</div>
<div className="w-full bg-gray-1 rounded-full h-3">
<motion.div
className={`h-3 rounded-full ${getColorClasses(levelColor).accent}`}
initial={{width: 0}}
animate={{width: `${(phaseData.score / phaseData.maxScore) * 100}%`}}
transition={{duration: 1,delay: 0.2}}
/>
</div>
</div>

<div className="bg-bg-light rounded-lg p-4 border border-gray-1">
<p className="text-text-secondary text-sm">
{phaseData.description}
</p>
</div>
</div>
);
})}
</div>
</motion.div>
)}
</div>

{/* Red Flag Assessment */}
<div className="bg-bg-primary rounded-2xl ernest-shadow overflow-hidden">
<SectionHeader
title="Red Flag Assessment"
section="redFlags"
icon={FiFlag}
/>
{expandedSections.redFlags && (
<motion.div
initial={{opacity: 0,height: 0}}
animate={{opacity: 1,height: 'auto'}}
className="p-6"
>
<div className="bg-red-50 border-2 border-red-200 rounded-xl p-6 mb-6">
<div className="flex items-start mb-4">
<SafeIcon icon={FiAlertTriangle} className="mr-3 text-red-600 mt-1 flex-shrink-0" />
<div>
<h4 className="font-semibold text-red-800 mb-2">
Check if any of these apply to you (regardless of your score):
</h4>
<p className="text-sm text-red-700">
If you check 3+ boxes,consider immediate systematic intervention regardless of your total score.
</p>
</div>
</div>
<div className="space-y-3">
{resultsGuide.redFlags.map((flag,index)=> (
<label key={index} className="flex items-start cursor-pointer group">
<input
type="checkbox"
className="mt-1 mr-3 text-red-600 focus:ring-red-500"
/>
<span className="text-text-secondary group-hover:text-text-primary transition-colors">
{flag}
</span>
</label>
))}
</div>
</div>
</motion.div>
)}
</div>

{/* Action Plan */}
<div className="bg-bg-primary rounded-2xl ernest-shadow overflow-hidden">
<SectionHeader
title="Your Personalized Action Plan"
section="actionPlan"
icon={FiPlayCircle}
/>
{expandedSections.actionPlan && (
<motion.div
initial={{opacity: 0,height: 0}}
animate={{opacity: 1,height: 'auto'}}
className="p-6"
>
<div className="bg-gradient-to-r from-primary-50 to-bg-cream rounded-xl p-6 border border-primary-200">
<h4 className="font-semibold text-text-primary mb-4">
{resultsGuide.nextStepsByCategory[category].title}
</h4>
<ul className="space-y-3">
{resultsGuide.nextStepsByCategory[category].steps.map((step,index)=> (
<li key={index} className="flex items-start">
<SafeIcon icon={FiCheckCircle} className="mr-3 mt-0.5 text-primary-600 flex-shrink-0" />
<span className="text-text-secondary">{step}</span>
</li>
))}
</ul>
</div>

<div className="mt-6 ernest-gradient rounded-xl p-6 text-white text-center">
<h4 className="font-semibold mb-2">Ready to tackle those challenges head-on?</h4>
<p className="text-primary-100 text-sm mb-4">
Ready to tackle those challenges head-on? If your assessment highlighted areas where you're struggling—or you're simply excited to accelerate your growth—Coach Ernest is here to help you create your personalized roadmap forward through a Bridge Framework strategy session.
</p>
<a
href="https://www.coachedbyernest.com/booking-calendar/initial-discovery-session-90-mins?utm_medium=page_links"
target="_blank"
rel="noopener noreferrer"
className="bg-bg-primary text-primary-600 px-6 py-2 rounded-lg font-semibold hover:bg-bg-light transition-colors ernest-hover inline-block"
>
Schedule Your Initial Discovery Session
</a>
</div>
</motion.div>
)}
</div>
</div>
);
};

export default DetailedResults;